// =============================================================================
// Reference Docs — the "📎 Attachments" button + panel (notes, photos, files
// kept against a record). One reusable pair for Quote / Order / Invoice /
// Shipment pages; the only per-page difference is entityType + entityId.
//
// Backend: /api/reference-docs (ReferenceDocsController). COMPLETELY SEPARATE
// from the PO-document pipeline (/api/Attachment) — no OCR, no coverage here.
//
// File upload is the 3-step SAS dance (same shape as the PO uploader):
//   1. POST /api/reference-docs            → { referenceDocId, sasUploadUrl }
//   2. PUT bytes to sasUploadUrl           (browser → Azure Blob, no auth hdr)
//   3. POST /api/reference-docs/{id}/confirm  (server checks REAL size + quota)
// Until step 3 succeeds nothing is shown — an abandoned upload leaves no row.
//
// Entitlements: 00060 open/view · 00061 add/upload · 00062 edit · 00063 delete
// (SQL/039). Button hides itself without 00060, so pages can render it
// unconditionally. Server messages are shown verbatim — they're written for
// users (TooLarge / QuotaExceeded / blocked type).
// =============================================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  FaDownload, FaFileAlt, FaImage, FaPaperclip, FaPen, FaPlus,
  FaStickyNote, FaTrashAlt, FaUpload,
} from "react-icons/fa";
import { apiFetch, hasAction } from "../utils/auth";

const API_URL = process.env.REACT_APP_API_URL;

// Quota rules (mirrors the server; server remains the authority).
const MAX_FILE_MB = 25;
const MAX_FILES_PER_RECORD = 25;
const MAX_TOTAL_MB = 100;

const fmtBytes = (b) => {
  if (b == null) return "";
  if (b < 1024 * 1024) return `${Math.max(1, Math.round(b / 1024))} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const days = Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString();
};

const docIcon = (doc) => {
  if (!doc.hasFile) return <FaStickyNote style={{ color: "#B45309" }} />;
  if ((doc.contentType || "").startsWith("image/")) return <FaImage style={{ color: "#2563EB" }} />;
  return <FaFileAlt style={{ color: "#64748B" }} />;
};

// Pull the user-facing message out of any reference-docs response body.
const errOf = (json, fallback) => json?.error || json?.message || fallback;

// =============================================================================
// AttachmentsButton — drop into a page's ribbon/action row. Owns the doc list
// (one GET on record load fills the badge) and the panel modal.
// =============================================================================
export default function AttachmentsButton({ entityType, entityId, style }) {
  const [docs, setDocs] = useState(null);     // null = not loaded yet
  const [show, setShow] = useState(false);

  const id = entityId == null ? "" : String(entityId);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const r = await apiFetch(
        `${API_URL}/api/reference-docs?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(id)}`
      );
      const list = await r.json().catch(() => null);
      setDocs(Array.isArray(list) ? list : []);
    } catch {
      setDocs([]);   // badge is best-effort; the panel retries on open
    }
  }, [entityType, id]);

  useEffect(() => { setDocs(null); load(); }, [load]);

  if (!hasAction("00060", true) || !id) return null;

  const count = Array.isArray(docs) ? docs.length : 0;

  return (
    <>
      <Button
        className="qw-btn qw-btn-sm"
        onClick={() => setShow(true)}
        title="Notes, photos and files kept against this record"
        style={{ background: "rgba(255,255,255,.18)", color: "#fff", border: "none", fontWeight: 600, ...style }}
      >
        <FaPaperclip style={{ marginRight: 6 }} /> Attachments
        {count > 0 && (
          <Badge pill bg="light" text="dark" style={{ marginLeft: 6, fontSize: 11 }}>{count}</Badge>
        )}
      </Button>
      <ReferenceDocsModal
        show={show}
        onHide={() => setShow(false)}
        entityType={entityType}
        entityId={id}
        docs={docs}
        reload={load}
      />
    </>
  );
}

// =============================================================================
// The panel itself (modal) — list, add-note, upload, edit, replace, delete.
// =============================================================================
function ReferenceDocsModal({ show, onHide, entityType, entityId, docs, reload }) {
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);        // user-visible status line ("Sending…")
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [editing, setEditing] = useState(null);  // { id, title, body }
  const [savingEdit, setSavingEdit] = useState(false);

  const uploadRef = useRef(null);
  const replaceRef = useRef(null);
  const replaceTarget = useRef(null);            // doc id whose bytes get swapped

  const canAdd = hasAction("00061", true);
  const canEdit = hasAction("00062", true);
  const canDelete = hasAction("00063", true);

  // Refresh the list every time the panel opens (badge load may be stale).
  useEffect(() => {
    if (show) { setErr(null); reload(); }
  }, [show, reload]);

  const files = useMemo(() => (docs || []).filter((d) => d.hasFile), [docs]);
  const totalBytes = useMemo(() => files.reduce((s, f) => s + (f.fileSizeBytes || 0), 0), [files]);

  // ── Add note ────────────────────────────────────────────────────────────────
  const saveNote = async () => {
    setSavingNote(true); setErr(null);
    try {
      const r = await apiFetch(`${API_URL}/api/reference-docs`, {
        method: "POST",
        body: JSON.stringify({ entityType, entityId, kind: "Note", title: noteTitle || null, body: noteBody || null }),
      });
      const json = await r.json().catch(() => null);
      if (!r.ok || !json?.success) throw new Error(errOf(json, "Could not save the note."));
      setNoteOpen(false); setNoteTitle(""); setNoteBody("");
      await reload();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSavingNote(false);
    }
  };

  // ── Upload file(s) — 3-step SAS flow per file ──────────────────────────────
  const uploadOne = async (file) => {
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      throw new Error(`File is ${(file.size / (1024 * 1024)).toFixed(0)} MB — exceeds the ${MAX_FILE_MB} MB limit.`);
    }
    const createRes = await apiFetch(`${API_URL}/api/reference-docs`, {
      method: "POST",
      body: JSON.stringify({
        entityType, entityId, kind: "File",
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
        fileSizeBytes: file.size,
      }),
    });
    const created = await createRes.json().catch(() => null);
    if (!createRes.ok || !created?.success || !created.sasUploadUrl) {
      throw new Error(errOf(created, `Could not allocate an upload slot for ${file.name}.`));
    }

    // Bytes go straight to Azure Blob — no auth header on this call.
    const putRes = await fetch(created.sasUploadUrl, {
      method: "PUT",
      headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });
    if (!putRes.ok) throw new Error(`Upload failed for ${file.name} (HTTP ${putRes.status}).`);

    setBusy(`Verifying ${file.name}…`);
    const confRes = await apiFetch(`${API_URL}/api/reference-docs/${created.referenceDocId}/confirm`, { method: "POST" });
    const conf = await confRes.json().catch(() => null);
    if (!confRes.ok || !conf?.success) throw new Error(errOf(conf, `Could not finalize ${file.name}.`));
  };

  const handleUpload = async (fileList) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    setErr(null);
    try {
      for (let i = 0; i < list.length; i++) {
        setBusy(`Sending ${list[i].name}${list.length > 1 ? ` (${i + 1} of ${list.length})` : ""}…`);
        await uploadOne(list[i]);
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(null);
      await reload();
    }
  };

  // ── Replace a file's content (old bytes stay live until confirm) ───────────
  const handleReplace = async (file) => {
    const targetId = replaceTarget.current;
    replaceTarget.current = null;
    if (!file || !targetId) return;
    setErr(null);
    try {
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        throw new Error(`File is ${(file.size / (1024 * 1024)).toFixed(0)} MB — exceeds the ${MAX_FILE_MB} MB limit.`);
      }
      setBusy(`Sending ${file.name}…`);
      const res = await apiFetch(`${API_URL}/api/reference-docs/${targetId}/replace-content`, {
        method: "POST",
        body: JSON.stringify({
          fileName: file.name,
          contentType: file.type || "application/octet-stream",
          fileSizeBytes: file.size,
        }),
      });
      const staged = await res.json().catch(() => null);
      if (!res.ok || !staged?.success || !staged.sasUploadUrl) {
        throw new Error(errOf(staged, "Could not stage the replacement."));
      }
      const putRes = await fetch(staged.sasUploadUrl, {
        method: "PUT",
        headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!putRes.ok) throw new Error(`Upload failed (HTTP ${putRes.status}).`);
      setBusy("Verifying…");
      const confRes = await apiFetch(`${API_URL}/api/reference-docs/${targetId}/confirm`, { method: "POST" });
      const conf = await confRes.json().catch(() => null);
      if (!confRes.ok || !conf?.success) throw new Error(errOf(conf, "Could not finalize the replacement."));
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(null);
      await reload();
    }
  };

  // ── Download / edit / delete ───────────────────────────────────────────────
  const download = async (doc) => {
    setErr(null);
    try {
      const r = await apiFetch(`${API_URL}/api/reference-docs/${doc.id}/download-url`);
      const json = await r.json().catch(() => null);
      if (!r.ok || !json?.success || !json.downloadUrl) throw new Error(errOf(json, "Could not get a download link."));
      window.open(json.downloadUrl, "_blank", "noopener");
    } catch (e) {
      setErr(e.message);
    }
  };

  const saveEdit = async () => {
    setSavingEdit(true); setErr(null);
    try {
      const r = await apiFetch(`${API_URL}/api/reference-docs/${editing.id}`, {
        method: "PUT",
        body: JSON.stringify({ title: editing.title || null, body: editing.body || null }),
      });
      if (!r.ok) {
        const json = await r.json().catch(() => null);
        throw new Error(errOf(json, "Could not save the change."));
      }
      setEditing(null);
      await reload();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const remove = async (doc) => {
    const label = doc.hasFile ? (doc.fileName || "this file") : "this note";
    if (!window.confirm(`Delete ${label}? This can't be undone.`)) return;
    setErr(null);
    try {
      const r = await apiFetch(`${API_URL}/api/reference-docs/${doc.id}`, { method: "DELETE" });
      if (!r.ok && r.status !== 204) throw new Error(`Delete failed (HTTP ${r.status}).`);
      await reload();
    } catch (e) {
      setErr(e.message);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const meterPct = Math.min(100, Math.round((totalBytes / (MAX_TOTAL_MB * 1024 * 1024)) * 100));

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 17, fontWeight: 700 }}>
          <FaPaperclip style={{ marginRight: 8, color: "#4F46E5" }} />
          Attachments — {entityType} #{entityId}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: 260 }}>
        {/* Toolbar: actions + quota meter */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          {canAdd && (
            <>
              <Button size="sm" variant="primary" disabled={!!busy} onClick={() => { setNoteOpen((v) => !v); setErr(null); }} style={{ fontWeight: 600 }}>
                <FaPlus style={{ marginRight: 5 }} /> Add note
              </Button>
              <Button size="sm" variant="outline-secondary" disabled={!!busy} onClick={() => uploadRef.current?.click()} style={{ fontWeight: 600 }}>
                <FaUpload style={{ marginRight: 5 }} /> Upload file
              </Button>
            </>
          )}
          <div style={{ marginLeft: "auto", textAlign: "right", fontSize: 11.5, color: "#8B8799" }}>
            {files.length} of {MAX_FILES_PER_RECORD} files{totalBytes > 0 ? ` · ${fmtBytes(totalBytes)} / ${MAX_TOTAL_MB} MB` : ""}
            <div style={{ width: 132, height: 6, borderRadius: 4, background: "#E7E4F0", marginTop: 4, marginLeft: "auto", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${meterPct}%`, background: "#6D28D9", borderRadius: 4 }} />
            </div>
          </div>
        </div>

        {/* Hidden pickers (must be in the DOM or `change` never fires) */}
        <input ref={uploadRef} type="file" multiple style={{ display: "none" }}
          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
          onChange={(e) => { handleUpload(e.target.files); e.target.value = ""; }} />
        <input ref={replaceRef} type="file" style={{ display: "none" }}
          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
          onChange={(e) => { handleReplace(e.target.files?.[0]); e.target.value = ""; }} />

        {err && <Alert variant="danger" dismissible onClose={() => setErr(null)} style={{ fontSize: 13 }}>{err}</Alert>}
        {busy && (
          <Alert variant="info" style={{ fontSize: 13 }}>
            <Spinner animation="border" size="sm" style={{ marginRight: 8 }} /> {busy}
          </Alert>
        )}

        {/* Add-note form */}
        {noteOpen && (
          <div style={{ border: "1px solid #E7E4F0", borderRadius: 10, padding: 12, marginBottom: 12, background: "#FAF9FE" }}>
            <Form.Control size="sm" placeholder="Title (optional)" value={noteTitle}
              onChange={(e) => setNoteTitle(e.target.value)} style={{ marginBottom: 8 }} />
            <Form.Control size="sm" as="textarea" rows={3} placeholder="Write the note…" value={noteBody}
              onChange={(e) => setNoteBody(e.target.value)} style={{ marginBottom: 8 }} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button size="sm" variant="outline-secondary" onClick={() => { setNoteOpen(false); setNoteTitle(""); setNoteBody(""); }}>Cancel</Button>
              <Button size="sm" variant="primary" disabled={savingNote || (!noteTitle.trim() && !noteBody.trim())} onClick={saveNote}>
                {savingNote ? <><Spinner animation="border" size="sm" /> Saving…</> : "Save note"}
              </Button>
            </div>
          </div>
        )}

        {/* List */}
        {docs === null ? (
          <div style={{ textAlign: "center", padding: 36, color: "#8B8799" }}>
            <Spinner animation="border" size="sm" /> Loading…
          </div>
        ) : docs.length === 0 ? (
          <div style={{ textAlign: "center", padding: 36, color: "#8B8799" }}>
            <div style={{ fontSize: 26, opacity: 0.6, marginBottom: 6 }}><FaPaperclip /></div>
            <div style={{ fontWeight: 600, color: "#57536A", fontSize: 14 }}>No documents yet</div>
            <div style={{ fontSize: 12.5 }}>Add a note or upload a file to get started.</div>
          </div>
        ) : (
          <div>
            {docs.map((doc) => (
              <div key={doc.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "11px 4px", borderBottom: "1px solid #EEECF6" }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, display: "grid", placeItems: "center", background: "#FAF9FE", border: "1px solid #E7E4F0", flexShrink: 0, fontSize: 15 }}>
                  {docIcon(doc)}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  {editing?.id === doc.id ? (
                    <div>
                      <Form.Control size="sm" placeholder="Title" value={editing.title}
                        onChange={(e) => setEditing({ ...editing, title: e.target.value })} style={{ marginBottom: 6 }} />
                      <Form.Control size="sm" as="textarea" rows={2} placeholder={doc.hasFile ? "Caption (optional)" : "Note text"}
                        value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} style={{ marginBottom: 6 }} />
                      <div style={{ display: "flex", gap: 8 }}>
                        <Button size="sm" variant="primary" disabled={savingEdit} onClick={saveEdit}>
                          {savingEdit ? "Saving…" : "Save"}
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => setEditing(null)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#1B1826", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        title={doc.hasFile ? doc.fileName : doc.title}>
                        {doc.hasFile ? (doc.fileName || doc.title || "File") : (doc.title || "Note")}
                      </div>
                      {/* Note text / file caption */}
                      {doc.body && (
                        <div style={{ fontSize: 12.5, color: "#57536A", whiteSpace: "pre-wrap", marginTop: 2 }}>{doc.body}</div>
                      )}
                      <div style={{ fontSize: 11.5, color: "#8B8799", marginTop: 2 }}>
                        {doc.hasFile ? fmtBytes(doc.fileSizeBytes) : "note"}
                        {doc.createdBy ? ` · added by ${doc.createdBy}` : ""} · {fmtWhen(doc.createdAt)}
                      </div>
                    </>
                  )}
                </div>
                {editing?.id !== doc.id && (
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    {doc.hasFile && (
                      <Button size="sm" variant="outline-secondary" title="Download" onClick={() => download(doc)}><FaDownload /></Button>
                    )}
                    {canEdit && (
                      <Button size="sm" variant="outline-secondary" title={doc.hasFile ? "Edit caption" : "Edit note"}
                        onClick={() => setEditing({ id: doc.id, title: doc.title || "", body: doc.body || "" })}><FaPen /></Button>
                    )}
                    {canEdit && doc.hasFile && (
                      <Button size="sm" variant="outline-secondary" title="Replace the file (old one stays until the new upload is verified)"
                        disabled={!!busy}
                        onClick={() => { replaceTarget.current = doc.id; replaceRef.current?.click(); }}><FaUpload /></Button>
                    )}
                    {canDelete && (
                      <Button size="sm" variant="outline-danger" title="Delete" onClick={() => remove(doc)}><FaTrashAlt /></Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
